import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy, Download, GripVertical, Plus, Save, Trash2 } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  api,
  type EventRow,
  type FullBackfillResponse,
  type KommoField,
  type KommoPipeline,
  type KommoStatus,
  type PipelineRow,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KommoFieldCombobox, type FieldOption } from '@/components/KommoFieldCombobox';
import { useToast } from '@/components/ui/toaster';

type Entity = 'lead' | 'contact' | 'company';

interface MappingItem {
  entity?: Entity;
  kommo_field_id?: number | string;
  kommo_field_code?: string;
  sheet_column: string;
  type: 'text' | 'number' | 'date' | 'datetime' | 'enum' | 'multi_enum' | 'json';
  /** Клиентский id для DnD-сортировки. На бэкенд не отправляется. */
  _uid?: string;
}

let uidCounter = 0;
function nextUid(): string {
  uidCounter += 1;
  return `row-${Date.now().toString(36)}-${uidCounter}`;
}

/** Убирает клиентские поля перед отправкой на бэкенд. */
function stripClientFields(items: MappingItem[]): Omit<MappingItem, '_uid'>[] {
  return items.map(({ _uid, ...rest }) => rest);
}

const LEAD_SYSTEM_FIELDS: MappingItem[] = [
  { entity: 'lead', kommo_field_code: 'name', sheet_column: 'Название', type: 'text' },
  { entity: 'lead', kommo_field_code: 'price', sheet_column: 'Цена', type: 'number' },
  { entity: 'lead', kommo_field_code: 'status_id', sheet_column: 'Этап (id)', type: 'text' },
  { entity: 'lead', kommo_field_code: 'created_at', sheet_column: 'Создана', type: 'datetime' },
  { entity: 'lead', kommo_field_code: 'updated_at', sheet_column: 'Обновлена', type: 'datetime' },
  { entity: 'lead', kommo_field_code: 'responsible_user_id', sheet_column: 'Ответственный', type: 'text' },
  { entity: 'lead', kommo_field_code: 'main_contact_id', sheet_column: 'Контакт (id)', type: 'text' },
  { entity: 'lead', kommo_field_code: 'tags', sheet_column: 'Теги', type: 'text' },
];

const CONTACT_SYSTEM_FIELDS: MappingItem[] = [
  { entity: 'contact', kommo_field_code: 'name', sheet_column: 'Контакт — имя', type: 'text' },
  { entity: 'contact', kommo_field_code: 'first_name', sheet_column: 'Контакт — first name', type: 'text' },
  { entity: 'contact', kommo_field_code: 'last_name', sheet_column: 'Контакт — last name', type: 'text' },
  { entity: 'contact', kommo_field_code: 'responsible_user_id', sheet_column: 'Контакт — отв.', type: 'text' },
  { entity: 'contact', kommo_field_code: 'created_at', sheet_column: 'Контакт — создан', type: 'datetime' },
];

const COMPANY_SYSTEM_FIELDS: MappingItem[] = [
  { entity: 'company', kommo_field_code: 'name', sheet_column: 'Компания', type: 'text' },
  { entity: 'company', kommo_field_code: 'responsible_user_id', sheet_column: 'Компания — отв.', type: 'text' },
  { entity: 'company', kommo_field_code: 'created_at', sheet_column: 'Компания — создана', type: 'datetime' },
];

const ALL_SYSTEM_FIELDS: MappingItem[] = [
  ...LEAD_SYSTEM_FIELDS,
  ...CONTACT_SYSTEM_FIELDS,
  ...COMPANY_SYSTEM_FIELDS,
];

/** A, B, ..., Z, AA, AB ... */
function colLetter(index: number): string {
  let n = index + 1;
  let s = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

const KOMMO_TYPE_MAP: Record<string, MappingItem['type']> = {
  text: 'text',
  textarea: 'text',
  numeric: 'number',
  checkbox: 'text',
  select: 'enum',
  multiselect: 'multi_enum',
  // Phone/Email — composite поля Kommo с enum (WORK/MOB/HOME), значения склеиваем через ", "
  multitext: 'multi_enum',
  date: 'date',
  url: 'text',
  date_time: 'datetime',
  price: 'number',
};

export function PipelineDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [mapping, setMapping] = useState<MappingItem[]>([]);
  const [pipelineIds, setPipelineIds] = useState<number[]>([]);
  // null = фильтра вообще нет, любая воронка пропускает все статусы.
  // { "8159299": [142, 143, ...] } = allow-list per воронка.
  const [statusFilter, setStatusFilter] = useState<Record<string, number[]> | null>(null);

  const { data: pipelines = [] } = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => api.get<PipelineRow[]>('/admin/pipelines'),
  });

  const pipeline = pipelines.find((p) => p.id === id);

  // Подтягиваем custom fields из Kommo для аккаунта pipeline'а
  const accountId = pipeline?.kommo_account.id;
  const { data: customFields = [], isLoading: cfLoading } = useQuery({
    queryKey: ['kommo-fields', accountId],
    queryFn: () => api.get<KommoField[]>(`/admin/accounts/${accountId}/kommo-fields`),
    enabled: Boolean(accountId),
  });

  const { data: contactFields = [] } = useQuery({
    queryKey: ['kommo-contact-fields', accountId],
    queryFn: () =>
      api.get<KommoField[]>(`/admin/accounts/${accountId}/kommo-contact-fields`),
    enabled: Boolean(accountId),
  });

  const { data: companyFields = [] } = useQuery({
    queryKey: ['kommo-company-fields', accountId],
    queryFn: () =>
      api.get<KommoField[]>(`/admin/accounts/${accountId}/kommo-company-fields`),
    enabled: Boolean(accountId),
  });

  // Подтягиваем список воронок Kommo для multi-select
  const { data: kommoPipelines = [] } = useQuery({
    queryKey: ['kommo-pipelines', accountId],
    queryFn: () => api.get<KommoPipeline[]>(`/admin/accounts/${accountId}/kommo-pipelines`),
    enabled: Boolean(accountId),
  });

  // Заголовки текущего Sheet — для подсказок в маппинг-редакторе
  const sheetId = pipeline?.sheet_target.id;
  const { data: sheetHeaders } = useQuery({
    queryKey: ['sheet-headers', sheetId],
    queryFn: () => api.get<{ headers: string[] }>(`/admin/sheets/${sheetId}/headers`),
    enabled: Boolean(sheetId),
  });

  // Синхронизация локального state с pipeline. Проставляем _uid каждой строке —
  // нужно для DnD-сортировки. На бэкенд _uid не отправляется (stripClientFields).
  useEffect(() => {
    if (pipeline) {
      setMapping(
        (pipeline.field_mapping as MappingItem[]).map((row) => ({ ...row, _uid: nextUid() })),
      );
      setPipelineIds(pipeline.pipeline_ids ?? []);
      setStatusFilter(pipeline.status_filter ?? null);
    }
  }, [pipeline?.id]);

  const saveMutation = useMutation({
    mutationFn: () =>
      api.put(`/admin/pipelines/${id}`, {
        field_mapping: stripClientFields(mapping),
        pipeline_ids: pipelineIds,
        status_filter: statusFilter,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelines'] });
      toast({
        variant: 'success',
        title: 'Сохранено',
        description: `Маппинг (${mapping.length} полей) и воронки (${pipelineIds.length || 'все'}) обновлены.`,
      });
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: 'Ошибка сохранения',
        description: (err as Error).message,
      });
    },
  });

  const fullBackfillMutation = useMutation({
    mutationFn: () => api.post<FullBackfillResponse>(`/admin/pipelines/${id}/full-backfill`),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['events', id] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast({
        variant: 'success',
        title: 'Загрузка запущена',
        description: `Просканировано ${data.scanned}, в очереди ${data.queued}, пропущено по статусу ${data.skipped_by_status}.`,
      });
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: 'Ошибка backfill',
        description: (err as Error).message,
      });
    },
  });

  const togglePipelineId = (pid: number): void => {
    setPipelineIds((prev) =>
      prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid],
    );
  };

  // Включить/выключить status_id для конкретной воронки в allow-list.
  const toggleStatusForPipeline = (pid: number, statusId: number): void => {
    setStatusFilter((prev) => {
      const base: Record<string, number[]> = { ...(prev ?? {}) };
      const key = String(pid);
      const arr = Array.isArray(base[key]) ? [...base[key]] : [];
      const idx = arr.indexOf(statusId);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(statusId);
      base[key] = arr;
      return base;
    });
  };

  // Установить весь allow-list воронки сразу (для «Выбрать все» / «Снять все»).
  const setStatusesForPipeline = (pid: number, statusIds: number[]): void => {
    setStatusFilter((prev) => ({ ...(prev ?? {}), [String(pid)]: statusIds }));
  };

  // Полностью убрать фильтр для воронки (станет «все статусы»).
  const clearStatusFilterForPipeline = (pid: number): void => {
    setStatusFilter((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      delete next[String(pid)];
      return Object.keys(next).length === 0 ? null : next;
    });
  };

  // Последние 20 событий по этому pipeline'у
  const { data: eventsResp } = useQuery({
    queryKey: ['events', id],
    queryFn: () =>
      api.get<{ events: EventRow[] }>(`/admin/events?pipeline_id=${id}&limit=20`),
    enabled: Boolean(id),
    refetchInterval: 10_000,
  });

  const sortByName = <T extends { name: string }>(arr: T[]): T[] =>
    [...arr].sort((a, b) => a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' }));

  // ВАЖНО: useSensors объявлен здесь, до любых ранних return, иначе при первом
  // рендере (pipeline ещё грузится) хуки не вызываются, а на следующем — вызываются,
  // и React падает с error #310 «Rendered more hooks than during the previous render».
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fieldOptions = useMemo<FieldOption[]>(() => {
    const sortedLeadSystem = [...LEAD_SYSTEM_FIELDS].sort((a, b) =>
      a.sheet_column.localeCompare(b.sheet_column, 'ru', { sensitivity: 'base' }),
    );
    const sortedContactSystem = [...CONTACT_SYSTEM_FIELDS].sort((a, b) =>
      a.sheet_column.localeCompare(b.sheet_column, 'ru', { sensitivity: 'base' }),
    );
    const sortedCompanySystem = [...COMPANY_SYSTEM_FIELDS].sort((a, b) =>
      a.sheet_column.localeCompare(b.sheet_column, 'ru', { sensitivity: 'base' }),
    );

    const mapCustom = (cf: KommoField, entity: Entity): FieldOption => ({
      id: cf.id,
      label: cf.name,
      hint: cf.code ?? `id ${cf.id}`,
      group: `${entity}-custom` as FieldOption['group'],
      entity,
      kommoType: KOMMO_TYPE_MAP[cf.type] ?? 'text',
    });

    const sysOpt = (sf: MappingItem, entity: Entity): FieldOption => ({
      code: sf.kommo_field_code,
      label: sf.sheet_column,
      hint: sf.kommo_field_code,
      group: `${entity}-system` as FieldOption['group'],
      entity,
    });

    return [
      ...sortedLeadSystem.map((sf) => sysOpt(sf, 'lead')),
      ...sortByName(customFields).map((cf) => mapCustom(cf, 'lead')),
      ...sortedContactSystem.map((sf) => sysOpt(sf, 'contact')),
      ...sortByName(contactFields).map((cf) => mapCustom(cf, 'contact')),
      ...sortedCompanySystem.map((sf) => sysOpt(sf, 'company')),
      ...sortByName(companyFields).map((cf) => mapCustom(cf, 'company')),
    ];
  }, [customFields, contactFields, companyFields]);

  if (!pipeline) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/pipelines">
            <ArrowLeft className="h-4 w-4" />К pipelines
          </Link>
        </Button>
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Pipeline не найден.
          </CardContent>
        </Card>
      </div>
    );
  }

  const addRow = (): void => {
    setMapping((m) => [...m, { sheet_column: '', type: 'text', _uid: nextUid() }]);
  };

  const updateRow = (i: number, patch: Partial<MappingItem>): void => {
    setMapping((m) => m.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  };

  const removeRow = (i: number): void => {
    setMapping((m) => m.filter((_, idx) => idx !== i));
  };

  const addSystem = (item: MappingItem): void => {
    if (
      mapping.some(
        (m) =>
          (m.entity ?? 'lead') === (item.entity ?? 'lead') &&
          m.kommo_field_code === item.kommo_field_code,
      )
    )
      return;
    setMapping((m) => [...m, { ...item, _uid: nextUid() }]);
  };

  // ---------- Drag-and-drop ----------
  // (sensors объявлен выше, до early return — иначе React error #310)

  const handleDragEnd = (e: DragEndEvent): void => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setMapping((items) => {
      const oldIndex = items.findIndex((r) => r._uid === active.id);
      const newIndex = items.findIndex((r) => r._uid === over.id);
      if (oldIndex < 0 || newIndex < 0) return items;
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  return (
    <div className="space-y-6">
      <Button asChild variant="ghost" size="sm" className="w-fit">
        <Link to="/pipelines">
          <ArrowLeft className="h-4 w-4" />К pipelines
        </Link>
      </Button>

      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{pipeline.display_name}</h1>
          <p className="text-sm text-muted-foreground">
            Маппинг полей Kommo на колонки Google Sheet
          </p>
        </div>
        <Badge variant={pipeline.status === 'active' ? 'success' : 'secondary'}>
          {pipeline.status === 'active' ? 'Active' : 'Paused'}
        </Badge>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Webhook URL</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded bg-muted px-3 py-2 text-xs">
              {pipeline.webhook_url}
            </code>
            <Button
              size="icon"
              variant="outline"
              onClick={() => void navigator.clipboard.writeText(pipeline.webhook_url)}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Вставьте этот URL в Kommo → Настройки → Интеграции → Webhooks.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Воронки Kommo</CardTitle>
        </CardHeader>
        <CardContent>
          {kommoPipelines.length === 0 ? (
            <p className="text-xs text-muted-foreground">Загрузка списка воронок…</p>
          ) : (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {kommoPipelines.map((p) => {
                  const checked = pipelineIds.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => togglePipelineId(p.id)}
                      className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                        checked
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:bg-accent'
                      }`}
                    >
                      {p.name}
                      {p.is_main ? ' ★' : ''}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                {pipelineIds.length === 0
                  ? 'Не выбрано ни одной — синхронизируются все воронки аккаунта'
                  : `Выбрано: ${pipelineIds.length}. Не забудьте «Сохранить» внизу.`}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {pipelineIds.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Статусы — какие сделки берём</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Для каждой воронки можно выбрать статусы, которые будут синхронизироваться. Сделки с
              другими статусами пропускаются (и при webhook'е, и при загрузке всех сделок). Если
              чекбоксы не настроены — берутся все статусы воронки.
            </p>
            {pipelineIds.map((pid) => {
              const kp = kommoPipelines.find((x) => x.id === pid);
              return (
                <PipelineStatusesSection
                  key={pid}
                  accountId={accountId ?? ''}
                  kommoPipelineId={pid}
                  kommoPipelineName={kp?.name ?? `Воронка ${pid}`}
                  filter={statusFilter}
                  onToggle={(sid) => toggleStatusForPipeline(pid, sid)}
                  onSetAll={(ids) => setStatusesForPipeline(pid, ids)}
                  onClear={() => clearStatusFilterForPipeline(pid)}
                />
              );
            })}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Загрузить все сделки</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Однократно подтянет все существующие сделки из выбранных воронок Kommo, отфильтрует по
            статусам выше и поставит в очередь на запись в Sheet. Соблюдает rate-limit Kommo (5
            запросов/сек) и rate-limit Google Sheets. Может занять минуты для тысяч лидов.
          </p>
          <Button
            size="sm"
            variant="outline"
            disabled={fullBackfillMutation.isPending}
            onClick={() => {
              const ok = window.confirm(
                'Запустить загрузку всех сделок?\n\nКнопка не повредит данные — каждая сделка обновится по lead.id (upsert). Но действие нельзя отменить, пока worker не разгребёт очередь.',
              );
              if (ok) fullBackfillMutation.mutate();
            }}
          >
            <Download className="h-4 w-4" />
            {fullBackfillMutation.isPending ? 'Сканируем Kommo…' : 'Загрузить все сделки'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Маппинг полей</CardTitle>
          <Button
            onClick={() => saveMutation.mutate()}
            size="sm"
            disabled={saveMutation.isPending}
          >
            <Save className="h-4 w-4" />
            {saveMutation.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            Колонка <code className="rounded bg-muted px-1">A</code> всегда содержит{' '}
            <code className="rounded bg-muted px-1">lead.id</code> (используется для upsert).
            Поля ниже идут в колонках B, C, D…
          </div>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={mapping.map((r) => r._uid ?? '')}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {mapping.map((row, i) => (
                  <SortableMappingRow
                    key={row._uid ?? i}
                    row={row}
                    index={i}
                    fieldOptions={fieldOptions}
                    sheetHeaders={sheetHeaders?.headers ?? []}
                    cfLoading={cfLoading}
                    onUpdate={updateRow}
                    onRemove={removeRow}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <div className="space-y-2">
            <Button size="sm" variant="outline" onClick={addRow}>
              <Plus className="h-4 w-4" />
              Добавить поле
            </Button>

            <div className="space-y-1">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Сделка
              </div>
              <div className="flex flex-wrap gap-1.5">
                {LEAD_SYSTEM_FIELDS.map((sf) => (
                  <Button
                    key={`lead-${sf.kommo_field_code}`}
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => addSystem(sf)}
                  >
                    + {sf.sheet_column}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Контакт
              </div>
              <div className="flex flex-wrap gap-1.5">
                {CONTACT_SYSTEM_FIELDS.map((sf) => (
                  <Button
                    key={`contact-${sf.kommo_field_code}`}
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => addSystem(sf)}
                  >
                    + {sf.sheet_column}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Компания
              </div>
              <div className="flex flex-wrap gap-1.5">
                {COMPANY_SYSTEM_FIELDS.map((sf) => (
                  <Button
                    key={`company-${sf.kommo_field_code}`}
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => addSystem(sf)}
                  >
                    + {sf.sheet_column}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Последние события</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 text-xs">
            {(eventsResp?.events ?? []).length === 0 && (
              <div className="text-muted-foreground">События ещё не приходили.</div>
            )}
            {eventsResp?.events.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-muted-foreground">#{e.id}</span>
                  <Badge variant={statusVariant(e.status)}>{e.status}</Badge>
                  <span>{e.kommo_event}</span>
                  {e.kommo_entity_id !== null && (
                    <span className="text-muted-foreground">lead {e.kommo_entity_id}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <span>{e.duration_ms ?? '—'} ms</span>
                  <span>{new Date(e.received_at).toLocaleTimeString()}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function statusVariant(status: EventRow['status']): 'success' | 'destructive' | 'warning' | 'secondary' | 'default' {
  switch (status) {
    case 'done':
      return 'success';
    case 'failed':
    case 'dead':
      return 'destructive';
    case 'processing':
      return 'warning';
    case 'pending':
      return 'secondary';
  }
}

interface PipelineStatusesSectionProps {
  accountId: string;
  kommoPipelineId: number;
  kommoPipelineName: string;
  filter: Record<string, number[]> | null;
  onToggle: (statusId: number) => void;
  onSetAll: (statusIds: number[]) => void;
  onClear: () => void;
}

function PipelineStatusesSection({
  accountId,
  kommoPipelineId,
  kommoPipelineName,
  filter,
  onToggle,
  onSetAll,
  onClear,
}: PipelineStatusesSectionProps): JSX.Element {
  const { data: statuses = [], isLoading } = useQuery<KommoStatus[]>({
    queryKey: ['kommo-statuses', accountId, kommoPipelineId],
    queryFn: () =>
      api.get<KommoStatus[]>(
        `/admin/accounts/${accountId}/kommo-statuses?pipeline_id=${kommoPipelineId}`,
      ),
    enabled: Boolean(accountId),
  });

  const key = String(kommoPipelineId);
  const allowList = filter && Array.isArray(filter[key]) ? filter[key] : null;
  const isFiltered = allowList !== null;

  return (
    <div className="rounded-md border border-border bg-card/40 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium">{kommoPipelineName}</div>
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground underline underline-offset-2"
            onClick={() => onSetAll(statuses.map((s) => s.id))}
          >
            Выбрать все
          </button>
          <span className="text-muted-foreground/40">·</span>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground underline underline-offset-2"
            onClick={() => onSetAll([])}
          >
            Снять все
          </button>
          {isFiltered && (
            <>
              <span className="text-muted-foreground/40">·</span>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground underline underline-offset-2"
                onClick={onClear}
                title="Убрать фильтр — берутся все статусы (включая будущие)"
              >
                Без фильтра
              </button>
            </>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="text-xs text-muted-foreground">Загрузка статусов…</div>
      ) : statuses.length === 0 ? (
        <div className="text-xs text-muted-foreground">Статусы не найдены.</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {statuses.map((s) => {
            const checked = isFiltered ? allowList!.includes(s.id) : true;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onToggle(s.id)}
                style={{
                  borderColor: checked ? s.color : undefined,
                  backgroundColor: checked ? `${s.color}33` : undefined,
                }}
                className={`rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                  checked
                    ? 'text-foreground'
                    : 'border-border text-muted-foreground hover:bg-accent'
                }`}
              >
                {s.name}
              </button>
            );
          })}
        </div>
      )}

      <div className="text-[11px] text-muted-foreground">
        {isFiltered
          ? `Выбрано ${allowList!.length} из ${statuses.length}. Новые статусы в Kommo НЕ попадут автоматически — нужно отметить вручную.`
          : 'Фильтр не задан — синхронизируются все статусы этой воронки (включая будущие).'}
      </div>
    </div>
  );
}

interface SortableMappingRowProps {
  row: MappingItem;
  index: number;
  fieldOptions: FieldOption[];
  sheetHeaders: string[];
  cfLoading: boolean;
  onUpdate: (i: number, patch: Partial<MappingItem>) => void;
  onRemove: (i: number) => void;
}

function SortableMappingRow({
  row,
  index,
  fieldOptions,
  sheetHeaders,
  cfLoading,
  onUpdate,
  onRemove,
}: SortableMappingRowProps): JSX.Element {
  const uid = row._uid ?? `row-${index}`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: uid,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };
  const entity = row.entity ?? 'lead';
  const entityLabel = entity === 'lead' ? 'Сделка' : entity === 'contact' ? 'Контакт' : 'Компания';
  const entityClass =
    entity === 'lead'
      ? 'bg-blue-500/10 text-blue-500'
      : entity === 'contact'
        ? 'bg-purple-500/10 text-purple-500'
        : 'bg-amber-500/10 text-amber-500';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="grid grid-cols-[24px_1fr_1fr_120px_36px] items-end gap-2 rounded-md border border-border bg-card/40 p-3"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="Перетащить строку"
        className="flex h-9 cursor-grab touch-none items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground flex items-center gap-2">
          Поле Kommo
          <span
            className={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${entityClass}`}
          >
            {entityLabel}
          </span>
        </Label>
        <KommoFieldCombobox
          options={fieldOptions}
          currentEntity={entity}
          value={(() => {
            if (row.kommo_field_id !== undefined) return `${entity}:id:${row.kommo_field_id}`;
            if (row.kommo_field_code) return `${entity}:code:${row.kommo_field_code}`;
            return '';
          })()}
          onChange={(opt) => {
            if (!opt) return;
            const isSystem = opt.group.endsWith('-system');
            if (isSystem) {
              const sys = ALL_SYSTEM_FIELDS.find(
                (f) => f.entity === opt.entity && f.kommo_field_code === opt.code,
              );
              onUpdate(index, {
                entity: opt.entity,
                kommo_field_code: opt.code,
                kommo_field_id: undefined,
                type: sys?.type ?? row.type,
              });
            } else {
              onUpdate(index, {
                entity: opt.entity,
                kommo_field_id: opt.id,
                kommo_field_code: undefined,
                type: (opt.kommoType as MappingItem['type']) ?? 'text',
              });
            }
          }}
          placeholder={cfLoading ? 'Загрузка…' : '— выбрать —'}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">
          Колонка в Sheet
          <span className="ml-1 text-[10px] text-muted-foreground/70">
            (#{colLetter(index + 1)})
          </span>
        </Label>
        <Input
          list={`sheet-headers-${index}`}
          value={row.sheet_column}
          placeholder="Название колонки"
          onChange={(e) => onUpdate(index, { sheet_column: e.target.value })}
        />
        <datalist id={`sheet-headers-${index}`}>
          {sheetHeaders
            .filter((h) => h && h !== 'lead_id')
            .map((h) => (
              <option key={h} value={h} />
            ))}
        </datalist>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Тип</Label>
        <select
          className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-xs"
          value={row.type}
          onChange={(e) => onUpdate(index, { type: e.target.value as MappingItem['type'] })}
        >
          <option value="text">text</option>
          <option value="number">number</option>
          <option value="date">date</option>
          <option value="datetime">datetime</option>
          <option value="enum">enum</option>
          <option value="multi_enum">multi_enum</option>
          <option value="json">json</option>
        </select>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="text-destructive hover:text-destructive"
        onClick={() => onRemove(index)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
